import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserAccountPasswordInput } from '@lub0v/santa-utils';
import { Button, Cluster, Columns, Input, Stack, showToast } from '@src/components';
import { resolveApiErrorKey } from '@src/i18n/apiError';
import { santaClient } from '@src/state/client';
import { translateFieldError, validateFieldError } from '@src/validation';

export function PasswordFields() {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const passwordField = password
    ? validateFieldError(new UserAccountPasswordInput(), 'password', { password })
    : undefined;
  const passwordError = passwordField ? translateFieldError(passwordField, t) : undefined;
  const confirmError =
    confirm && confirm !== password ? t('account.passwordsDiffer') : undefined;

  async function save() {
    if (passwordError || confirmError || !password || !confirm) {
      setError(passwordError || confirmError || t('validation.formErrors'));
      return;
    }
    setBusy(true);
    const res = await santaClient.accountUpdatePassword({ password });
    setBusy(false);
    if (res.ok) {
      setPassword('');
      setConfirm('');
      setError(undefined);
      showToast(t('account.passwordSaved'), 'success');
      return;
    }
    setError(t(resolveApiErrorKey(res.error?.message || '')));
  }

  return (
    <Stack gap={4} wide>
      <Columns columns={2} gap={4}>
        <Input
          value={password}
          onValueChange={(value) => {
            setError(undefined);
            setPassword(value);
          }}
          name="password"
          label={t('account.newPassword')}
          password
          autoComplete="new-password"
          error={passwordError || error}
          reserveSpace
          testId="account-password"
        />
        <Input
          value={confirm}
          onValueChange={(value) => {
            setError(undefined);
            setConfirm(value);
          }}
          name="confirm_password"
          label={t('account.confirmPassword')}
          password
          autoComplete="new-password"
          error={confirmError}
          reserveSpace
          testId="account-password-confirm"
        />
      </Columns>
      <Cluster justify="end" wide>
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            void save();
          }}
          loading={busy}
          testId="account-password-save"
        >
          {t('account.passwordUpdate')}
        </Button>
      </Cluster>
    </Stack>
  );
}
